import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AddOn } from '../../types/Package';
import { loadAddonsFromServer, saveAddonsToServer } from '../../utils/AddonAPI';
import './CategoryManagement.css';
import './ui-enhancements.css';

const AddonManagement: React.FC = () => {
  const [addons, setAddons] = useState<AddOn[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState<AddOn | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    pricingType: 'fixed' as 'fixed' | 'per-hour' | 'per-each',
    currency: 'R' as 'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD',
    description: '',
    showFromPrice: false,
  });

  useEffect(() => {
    loadAddonsList();
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        setShowModal(false);
        resetForm();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
    };
  }, [showModal]);

  const loadAddonsList = async () => {
    try {
      const serverAddons = await loadAddonsFromServer();
      console.log('📦 AddonManagement: Loaded addons:', serverAddons.length, serverAddons.map(a => a.name));
      setAddons(serverAddons);
    } catch (error) {
      console.error('Error loading addons:', error);
      setAddons([]);
    }
  };

  const saveAddonsList = async (addonsToSave: AddOn[]) => {
    try {
      const success = await saveAddonsToServer(addonsToSave);
      if (success) {
        console.log('💾 Addons saved to server');
      } else {
        console.log('⚠️ Server save failed, saved to localStorage');
      }
      setAddons(addonsToSave);
      window.dispatchEvent(new Event('addons_updated'));
    } catch (error) {
      console.error('Error saving addons:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Addon name is required');
      return;
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      alert('Valid price is required');
      return;
    }

    const newAddon: AddOn = {
      id: editingAddon?.id || `addon-${Date.now()}`,
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      pricingType: formData.pricingType,
      currency: formData.currency,
      description: formData.description.trim() || undefined,
      showFromPrice: formData.showFromPrice,
    };

    let updatedAddons: AddOn[];
    
    if (editingAddon) {
      updatedAddons = addons.map(addon => 
        addon.id === editingAddon.id ? newAddon : addon
      );
    } else {
      updatedAddons = [...addons, newAddon];
    }

    saveAddonsList(updatedAddons);
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      pricingType: 'fixed',
      currency: 'R',
      description: '',
      showFromPrice: false,
    });
    setEditingAddon(null);
  };

  const editAddon = (addon: AddOn) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      price: addon.price?.toString() || '',
      pricingType: addon.pricingType || 'fixed',
      currency: addon.currency || 'R',
      description: addon.description || '',
      showFromPrice: addon.showFromPrice || false,
    });
    setShowModal(true);
  };

  const deleteAddon = (addonId: string) => {
    const addon = addons.find(a => a.id === addonId);
    if (!addon) return;

    if (window.confirm(`Are you sure you want to delete the addon "${addon.name}"?`)) {
      const updatedAddons = addons.filter(a => a.id !== addonId);
      saveAddonsList(updatedAddons);
    }
  };

  const getPricingTypeLabel = (type: string) => {
    switch (type) {
      case 'fixed': return 'Fixed Price';
      case 'per-hour': return 'Per Hour';
      case 'per-each': return 'Per Each';
      default: return type;
    }
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'R': 'R',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
    };
    return symbols[currency] || currency;
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Package add-ons</h2>
        <div className="section-controls">
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus"></i> Add New Addon
          </button>
        </div>
      </div>

      <div className="categories-list">
        {addons.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-plus-circle"></i>
            <h3>No Addons Yet</h3>
            <p>Create your first addon to offer additional services like hair & makeup, lighting, studio time, etc.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus"></i> Create Addon
            </button>
          </div>
        ) : (
          <div className="categories-container">
            {addons.map((addon) => (
              <div key={addon.id} className="category-item">
                <div className="category-info">
                  <div className="category-details">
                    <h3 className="category-name">{addon.name}</h3>
                    <p className="category-description">{addon.description || 'No description'}</p>
                    <div className="category-meta">
                      <span className="category-slug">
                        {addon.showFromPrice ? 'from ' : ''}{getCurrencySymbol(addon.currency || 'R')}{addon.price?.toFixed(2) || '0.00'} 
                        ({getPricingTypeLabel(addon.pricingType || 'fixed')})
                      </span>
                      {addon.currency && addon.currency !== 'R' && (
                        <span className="category-slug">Currency: {addon.currency}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="category-actions">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => editAddon(addon)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteAddon(addon.id!)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Addon Modal */}
      {showModal && createPortal(
        <div 
          className="addon-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              resetForm();
            }
          }}
        >
          <div className="addon-modal" role="dialog" aria-modal="true" aria-label={editingAddon ? 'Edit add-on' : 'Add add-on'}>
            <div className="modal-header">
              <h3>{editingAddon ? 'Edit Addon' : 'Add New Addon'}</h3>
              <span className="close" onClick={() => {setShowModal(false); resetForm();}}>&times;</span>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="addon-name">Addon Name</label>
                  <input
                    type="text"
                    id="addon-name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Hair and Makeup, Studio Time, Extra Print"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addon-pricing-type">Pricing Type</label>
                  <select
                    id="addon-pricing-type"
                    value={formData.pricingType}
                    onChange={(e) => setFormData({...formData, pricingType: e.target.value as any})}
                    required
                  >
                    <option value="fixed">Fixed Price (e.g., Hair and Makeup @ R2000)</option>
                    <option value="per-hour">Per Hour (e.g., Studio Time @ R2500/hour)</option>
                    <option value="per-each">Per Each (e.g., Extra Print @ R50/each)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="addon-price">Price</label>
                  <input
                    type="number"
                    id="addon-price"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                  <small>
                    {formData.pricingType === 'fixed' && 'Total price for this addon'}
                    {formData.pricingType === 'per-hour' && 'Price per hour'}
                    {formData.pricingType === 'per-each' && 'Price per unit'}
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="addon-currency">Currency</label>
                  <select
                    id="addon-currency"
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value as any})}
                    required
                  >
                    <option value="R">South African Rand (R)</option>
                    <option value="USD">US Dollar ($)</option>
                    <option value="EUR">Euro (€)</option>
                    <option value="GBP">British Pound (£)</option>
                    <option value="CAD">Canadian Dollar (C$)</option>
                    <option value="AUD">Australian Dollar (A$)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="addon-description">Description</label>
                  <textarea
                    id="addon-description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    placeholder="Addon description (optional)"
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.showFromPrice}
                      onChange={(e) => setFormData({...formData, showFromPrice: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Show "from" prefix before price (e.g., "from R2000")</span>
                  </label>
                  <small style={{ color: '#666', marginLeft: '26px', display: 'block', marginTop: '4px' }}>
                    Use this when pricing requires external quoting and you want to show a starting price
                  </small>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => {setShowModal(false); resetForm();}}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingAddon ? 'Update Addon' : 'Save Addon'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default AddonManagement;

